/**
 * Phase 5 — MobileBottomNav
 *
 * Extracted from Layout.tsx (lines 462–539) and extended with:
 *  - Route-registry-driven tab slots (via `nav.tabBarPriority`)
 *  - localStorage-persisted user reordering (key: nutriflow_tab_bar_order)
 *  - MoreSheet for non-bar routes
 *
 * Design decisions:
 *  D-3: This component is `lg:hidden` only — bar never appears on desktop.
 *  R-3 (plan §6.3): `isPlatformAdminWorkspace` keeps the hardcoded admin tabs.
 *
 * WCAG:
 *  - Touch targets ≥ 48 px (min-h-[48px])
 *  - aria-current="page" on active non-menu items
 *  - aria-expanded on the "Mehr" button
 *  - Badge count announced only when > 0
 */

import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Menu, Building2, Tag, ShieldAlert } from 'lucide-react';
import { useMemo, useState, useEffect } from 'react';
import type { LucideIcon } from 'lucide-react';
import { ROUTES } from '@/features/route-registry';
import { MoreSheet } from '@/components/MoreSheet';

const TAB_BAR_ORDER_KEY = 'nutriflow_tab_bar_order';
const BAR_SLOT_COUNT = 4;

export interface MobileBottomNavProps {
  isPlatformAdminWorkspace: boolean;
  unreadCount?: number;
  navOpen: boolean;
  onNavOpenChange: (open: boolean) => void;
}

interface TabItem {
  icon: LucideIcon;
  label: string;
  href: string;
  badge?: number;
  routeId?: string;
}

function useTabBarOrder(): string[] | null {
  const [order, setOrder] = useState<string[] | null>(() => {
    try {
      const stored = localStorage.getItem(TAB_BAR_ORDER_KEY);
      if (stored) {
        const parsed: unknown = JSON.parse(stored);
        if (Array.isArray(parsed) && parsed.every((v) => typeof v === 'string')) {
          return parsed as string[];
        }
      }
    } catch {
      // ignore
    }
    return null;
  });

  useEffect(() => {
    function onStorage(e: StorageEvent) {
      if (e.key === TAB_BAR_ORDER_KEY) {
        try {
          const parsed: unknown = e.newValue ? JSON.parse(e.newValue) : null;
          setOrder(
            Array.isArray(parsed) && parsed.every((v) => typeof v === 'string')
              ? (parsed as string[])
              : null,
          );
        } catch {
          setOrder(null);
        }
      }
    }
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  return order;
}

export function MobileBottomNav({
  isPlatformAdminWorkspace,
  unreadCount = 0,
  navOpen,
  onNavOpenChange,
}: MobileBottomNavProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = location.pathname;
  const tabBarOrder = useTabBarOrder();

  // Derive the 4 visible tab slots from the route registry + user order.
  const dynamicTabs = useMemo<TabItem[]>(() => {
    // If user has a custom order, use that; otherwise fall back to tabBarPriority.
    const ordered = tabBarOrder
      ? ROUTES.filter((r) => tabBarOrder.includes(r.id) && r.nav).sort(
          (a, b) => tabBarOrder.indexOf(a.id) - tabBarOrder.indexOf(b.id),
        )
      : ROUTES.filter((r) => r.nav?.tabBarPriority != null).sort(
          (a, b) =>
            (a.nav!.tabBarPriority ?? 0) - (b.nav!.tabBarPriority ?? 0),
        );

    return ordered.slice(0, BAR_SLOT_COUNT).map((r) => ({
      icon: r.nav!.icon,
      label: r.nav!.label,
      href: r.path,
      badge: r.nav!.badgeKey === 'unread-messages' ? unreadCount : undefined,
      routeId: r.id,
    }));
  }, [tabBarOrder, unreadCount]);

  // Platform-admin workspace: hardcoded admin tabs (per plan §6.3 R-3).
  const adminTabs: TabItem[] = [
    { icon: Building2, label: 'Kunden',  href: '/admin' },
    { icon: Tag,       label: 'Preise',  href: '/admin/pricing' },
    { icon: ShieldAlert, label: 'Audit', href: '/admin/audit-log' },
  ];

  const tabs: TabItem[] = isPlatformAdminWorkspace ? adminTabs : dynamicTabs;
  const allItems = [
    ...tabs,
    { icon: Menu, label: t('nav.mobile.menu'), href: '__menu__' },
  ];

  return (
    <>
      <nav
        className="lg:hidden fixed bottom-0 left-0 right-0 bg-card/90 backdrop-blur-lg border-t border-border/50 z-40 safe-area-bottom"
        aria-label="Mobile Navigation"
      >
        <div className="flex">
          {allItems.map((item) => {
            const isMenu = item.href === '__menu__';
            const isActive = isMenu ? navOpen : currentPath.startsWith(item.href);
            const ariaLabel =
              item.badge && item.badge > 0
                ? `${item.label} (${item.badge} ${item.badge === 1 ? 'ungelesen' : 'ungelesene'})`
                : item.label;

            return (
              <button
                key={item.href}
                type="button"
                onClick={() => {
                  if (isMenu) {
                    onNavOpenChange(!navOpen);
                  } else {
                    onNavOpenChange(false);
                    navigate(item.href);
                  }
                }}
                aria-label={ariaLabel}
                aria-current={!isMenu && isActive ? 'page' : undefined}
                aria-expanded={isMenu ? navOpen : undefined}
                className={`flex-1 flex flex-col items-center gap-0.5 min-h-[48px] py-2 pt-3 transition-colors relative focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-inset rounded-xl ${
                  isActive ? 'text-primary bg-primary/10' : 'text-muted-foreground'
                }`}
              >
                {isActive && (
                  <span
                    aria-hidden="true"
                    className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-0.5 bg-primary rounded-full"
                  />
                )}
                <div className="relative">
                  <item.icon
                    aria-hidden="true"
                    className={`w-5 h-5 ${isActive ? 'scale-105' : ''} transition-transform`}
                  />
                  {item.badge != null && item.badge > 0 && (
                    <span
                      aria-hidden="true"
                      className="absolute -top-1.5 -right-2.5 bg-primary text-white text-[9px] font-bold rounded-full min-w-[16px] h-[16px] flex items-center justify-center px-1"
                    >
                      {item.badge > 99 ? '99+' : item.badge}
                    </span>
                  )}
                </div>
                <span className="text-[10px] font-medium leading-none">{item.label}</span>
              </button>
            );
          })}
        </div>
      </nav>

      {/* MoreSheet — only in non-admin mode */}
      {!isPlatformAdminWorkspace && (
        <MoreSheet
          open={navOpen}
          onOpenChange={onNavOpenChange}
          tabBarOrder={tabBarOrder}
        />
      )}
    </>
  );
}
